import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Flame, Trophy, Clock, BookMarked, ShieldOff, VolumeX, Lock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getStudentProfile, toggleFollow, setUserBlock } from "@/lib/community.functions";
import { LoadingState, ErrorState } from "@/components/states";

export const Route = createFileRoute("/_app/student/$id")({
  component: StudentPage,
  head: () => ({
    meta: [
      { title: "Profilo studente · Lumen" },
      { name: "description", content: "Statistiche di studio, XP, serie e appunti condivisi dello studente." },
      { property: "og:title", content: "Profilo studente · Lumen" },
      { property: "og:description", content: "XP, livello, serie e appunti condivisi." },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
});

function StudentPage() {
  const { id } = useParams({ from: "/_app/student/$id" });
  const { t } = useTranslation();
  const qc = useQueryClient();
  const load = useServerFn(getStudentProfile);
  const follow = useServerFn(toggleFollow);
  const block = useServerFn(setUserBlock);

  const q = useQuery({ queryKey: ["community", "student", id], queryFn: () => load({ data: { userId: id } }) });

  const followMutation = useMutation({
    mutationFn: () => follow({ data: { userId: id } }),
    onSuccess: (res) => {
      if ("error" in res && res.error === "private") toast.error(t("community.privateProfile"));
      qc.invalidateQueries({ queryKey: ["community"] });
    },
  });
  const blockMutation = useMutation({
    mutationFn: (kind: "block" | "mute" | "none") => block({ data: { userId: id, kind } }),
    onSuccess: (_r, kind) => {
      toast.success(t(kind === "block" ? "community.privacy.blocked" : kind === "mute" ? "community.privacy.muted" : "community.privacy.restored"));
      qc.invalidateQueries({ queryKey: ["community"] });
    },
  });

  if (q.isLoading) return <div className="px-5 pt-8"><LoadingState /></div>;
  if (q.isError) return <div className="px-5 pt-8"><ErrorState onRetry={() => q.refetch()} /></div>;

  const data = q.data;
  if (!data || data.status !== "ok") {
    return (
      <div className="space-y-4 px-5 pt-8">
        <BackLink />
        <Card className="space-y-2 p-6 text-center">
          <Lock className="mx-auto h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {data?.status === "private" ? t("community.privateProfile") : t("community.student.unavailable")}
          </p>
        </Card>
      </div>
    );
  }

  const p = data.profile;
  const name = p.username ?? p.full_name ?? t("community.student.anonymous");

  return (
    <div className="space-y-4 px-5 pt-8">
      <BackLink />
      <Card className="tone-sky space-y-4 p-5">
        <div className="flex items-center gap-3">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-primary/15 text-lg font-bold uppercase">
            {name.slice(0, 2)}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-bold">{name}</h1>
            <p className="truncate text-xs text-muted-foreground">
              {t("community.student.followers", { count: data.followers })} · {t("community.student.followingCount", { count: data.followingCount })}
            </p>
          </div>
        </div>

        {data.showStats ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat icon={<Trophy className="h-4 w-4" />} label={t("community.board.xp")} value={String(p.total_xp ?? 0)} />
            <Stat icon={<Trophy className="h-4 w-4" />} label={t("community.student.level")} value={String(p.level ?? 1)} />
            <Stat icon={<Flame className="h-4 w-4" />} label={t("community.student.streak")} value={String(p.streak_days ?? 0)} />
            <Stat icon={<Clock className="h-4 w-4" />} label={t("community.student.week")} value={`${data.weeklyMinutes}m`} />
          </div>
        ) : (
          <p className="rounded-2xl bg-background/70 p-3 text-xs text-muted-foreground">{t("community.student.statsHidden")}</p>
        )}

        {!data.isMe && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={data.following ? "secondary" : "default"} onClick={() => followMutation.mutate()}>
              {data.following ? t("community.unfollow") : t("community.follow")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => blockMutation.mutate(data.muted ? "none" : "mute")}>
              <VolumeX className="mr-1 h-3.5 w-3.5" />
              {data.muted ? t("community.privacy.remove") : t("community.privacy.mute")}
            </Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => blockMutation.mutate("block")}>
              <ShieldOff className="mr-1 h-3.5 w-3.5" />{t("community.privacy.block")}
            </Button>
          </div>
        )}
      </Card>

      <Card className="space-y-2 p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold"><BookMarked className="h-4 w-4" />{t("community.student.notes")}</h2>
        {!data.notes.length && <p className="text-xs text-muted-foreground">{t("community.student.notesEmpty")}</p>}
        {data.notes.map((n) => (
          <Link key={n.id} to="/community" className="block">
            <div className="rounded-2xl bg-muted/40 p-3 transition-colors hover:bg-muted/70">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{n.title}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {n.subject} · {t(`community.notes.docTypes.${n.doc_type ?? "notes"}`)} · {t("community.notes.views", { count: n.views ?? 0 })}
                  </p>
                </div>
                {n.status !== "approved" && <Badge variant="secondary" className="shrink-0 text-xs">{t(`community.notes.status.${n.status}`)}</Badge>}
              </div>
            </div>
          </Link>
        ))}
      </Card>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-background/70 p-3 text-center">
      <span className="mx-auto mb-1 flex items-center justify-center text-muted-foreground">{icon}</span>
      <p className="text-base font-bold tabular-nums">{value}</p>
      <p className="truncate text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Link to="/community" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
      <ArrowLeft className="h-3.5 w-3.5" />{t("community.title")}
    </Link>
  );
}
